/**
 * BNPL beta cohort hook
 *
 * Loads the practice/insurer allowlist from `appInfo/bnplCohort` (ERS-5) once on mount. A missing
 * document, an empty document, or a read failure all resolve to `cohort: null` — never an error
 * state — so `isInBnplBetaCohort` treats a config outage the same as "not in the cohort" instead of
 * blocking the insurance-select screen.
 */

import type { BnplCohortConfig } from '@physiofit-digital/physiofit_types';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../firebaseConfig';

export type BnplCohortResult =
  | { status: 'loading' }
  | { status: 'ready'; cohort: BnplCohortConfig | null };

const bnplCohortDocRef = () => doc(db, 'appInfo', 'bnplCohort');

/**
 * Loads the BNPL beta cohort config once on mount.
 *
 * @return {Object} Loading or ready result; `cohort` is null when unset/unreadable.
 */
export function useBnplCohort(): BnplCohortResult {
  const [result, setResult] = useState<BnplCohortResult>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const snap = await getDoc(bnplCohortDocRef());
        if (cancelled) {
          return;
        }
        if (!snap.exists()) {
          setResult({ status: 'ready', cohort: null });
          return;
        }
        setResult({ status: 'ready', cohort: snap.data() as BnplCohortConfig });
      } catch (error) {
        if (!cancelled) {
          // Still fail-safe (no cohort means no BNPL), but not silent: without this log a missing
          // rules deploy and an unconfigured cohort are the same nothing.
          console.error('[onboarding] appInfo/bnplCohort read failed; treating as not in cohort', error);
          setResult({ status: 'ready', cohort: null });
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  return result;
}
